export const ObjectivePeriodStatus = {
    DRAFT: 'Draft',
    ACTIVE: 'Active',
    DEACTIVATED: 'Deactivated',
    COMPLETED: 'Completed',
} as const;

export type ObjectivePeriodStatusType =
    (typeof ObjectivePeriodStatus)[keyof typeof ObjectivePeriodStatus];

/**
 * Which statuses a period may move to from the one it currently holds, mirroring the backend's
 * own table: a draft is launched, an active period is either deactivated or completed, a
 * deactivated one is reactivated, and Completed is terminal. Anything else answers 409
 * (`ObjectivePeriodErrorCode.INVALID_STATUS_TRANSITION`), so the row's menu and the modal's
 * status field both read this map rather than each spelling the rules out again.
 */
export const OBJECTIVE_PERIOD_STATUS_TRANSITIONS: Readonly<
    Record<ObjectivePeriodStatusType, readonly ObjectivePeriodStatusType[]>
> = {
    [ObjectivePeriodStatus.DRAFT]: [ObjectivePeriodStatus.ACTIVE],
    [ObjectivePeriodStatus.ACTIVE]: [
        ObjectivePeriodStatus.DEACTIVATED,
        ObjectivePeriodStatus.COMPLETED,
    ],
    [ObjectivePeriodStatus.DEACTIVATED]: [ObjectivePeriodStatus.ACTIVE],
    [ObjectivePeriodStatus.COMPLETED]: [],
};

/**
 * The statuses a brand new period may be saved with: it is read as a draft that may be activated in
 * the same move, the rule the backend applies on creation.
 */
export const OBJECTIVE_PERIOD_CREATION_STATUSES: readonly ObjectivePeriodStatusType[] = [
    ObjectivePeriodStatus.DRAFT,
    ...OBJECTIVE_PERIOD_STATUS_TRANSITIONS[ObjectivePeriodStatus.DRAFT],
];

/**
 * The status a new period starts on: it is offered to objectives as soon as it is saved, unless it
 * is kept as a draft. The API applies the same default to a payload that names no status.
 */
export const OBJECTIVE_PERIOD_DEFAULT_CREATION_STATUS: ObjectivePeriodStatusType =
    ObjectivePeriodStatus.ACTIVE;

// Server data is cast into `ObjectivePeriodStatusType` without runtime validation, so a status
// outside the members above can still reach these helpers despite the type.
export const isObjectivePeriodStatusTransitionAllowed = (
    from: ObjectivePeriodStatusType,
    to: ObjectivePeriodStatusType,
): boolean =>
    Object.hasOwn(OBJECTIVE_PERIOD_STATUS_TRANSITIONS, from) &&
    OBJECTIVE_PERIOD_STATUS_TRANSITIONS[from].includes(to);

/** A completed period is frozen: only its deletion is still accepted. */
export const isObjectivePeriodCompleted = (status: ObjectivePeriodStatusType): boolean =>
    status === ObjectivePeriodStatus.COMPLETED;

/** The `error` names the API answers with; `useError` translates them through `errors.<name>`. */
export const ObjectivePeriodErrorCode = {
    DUPLICATE_NAME: 'ObjectivePeriodDuplicateNameError',
    INVALID_STATUS_TRANSITION: 'ObjectivePeriodInvalidStatusTransitionError',
    COMPLETED_CANNOT_MODIFY: 'CompletedObjectivePeriodCannotModify',
} as const;
